import { BANNER_COUNTRIES, savedBannerRegions, regionsForCountry } from "../lib/bannerRegions";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  uploadBannerImage, getBannerRegions,
  startBannerCheckout,
  confirmBannerPayment,
  getMyBanners,
  getIsAdmin,
  validateBannerFile,
} from "../lib/bannerApi";

const RULES = [
  "Be reasonably appropriate and relevant to the RoadRnr audience.",
  "Accurately represent what is advertised.",
  "Only use content you have the rights to.",
  "Link to a legitimate, safe HTTPS destination.",
  "Not be unlawful, fraudulent, misleading, hateful, sexually explicit or malicious.",
  "Comply with the RoadRnr Terms.",
];

const STATUS_STYLE = {
  active: "border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e]",
  expired: "border-white/15 bg-white/5 text-white/60",
  hidden: "border-[#FFB000]/40 bg-[#FFB000]/10 text-[#FFB000]",
  removed: "border-[#ff2a00]/40 bg-[#ff2a00]/10 text-[#ff2a00]",
  draft: "border-white/15 bg-white/5 text-white/60",
};

function timeLeft(ends) {
  if (!ends) return null;
  const ms = new Date(ends).getTime() - Date.now();
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d >= 1 ? `${d}d ${h}h left` : `${h}h left`;
}

export default function Advertise() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [url, setUrl] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reuseId, setReuseId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [bannerRegions, setBannerRegions] = useState(() => savedBannerRegions());
  const [locationsUsingSaved, setLocationsUsingSaved] = useState(true);
  const [targetCountryCode, setTargetCountryCode] = useState("");
  const [targetRegionCode, setTargetRegionCode] = useState("");
  useEffect(() => {
    let alive = true;
    if (user?.id) getBannerRegions({ withStatus: true }).then(({ regions, usingSaved }) => {
      if (!alive) return;
      setBannerRegions(regions);
      setLocationsUsingSaved(usingSaved);
    }).catch(() => { if (alive) setLocationsUsingSaved(true); });
    return () => { alive = false; };
  }, [user?.id]);

  const loadAds = () => {
    if (!user) return;
    getMyBanners().then(setAds).catch(() => setAds([]));
  };

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) {
      confirmBannerPayment(sid).finally(() => {
        window.history.replaceState({}, "", "/advertise");
        loadAds();
      });
    } else {
      loadAds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user) getIsAdmin().then(setIsAdmin);
    else setIsAdmin(false);
  }, [user]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validateBannerFile(f);
    if (err) {
      e.target.value = "";
      return toast.error(err);
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!user) {
      navigate("/login?return=/advertise");
      return;
    }
    if (!reuseId && !file) return toast.error("Please choose a banner image.");
    if (!url.trim().toLowerCase().startsWith("https://"))
      return toast.error("Destination must be an HTTPS URL.");
    if (!confirmed) return toast.error("Please confirm the banner rules.");
    if ((!isAdmin && !bannerRegions.some((region) => region.country_code === targetCountryCode && region.region_code === targetRegionCode)) ||
        (isAdmin && targetRegionCode && !bannerRegions.some((region) => region.country_code === targetCountryCode && region.region_code === targetRegionCode)))
      return toast.error("Please choose a country and state/region for this banner.");
    setBusy(true);
    try {
      let imagePath, displayImagePath;
      if (file) {
        const up = await uploadBannerImage(file);
        imagePath = up.imagePath;
        displayImagePath = up.displayImagePath;
      } else {
        const existing = ads.find((a) => a.id === reuseId);
        // Reusing existing creative: backend keeps the current image when omitted is
        // not supported, so we require re-upload only if changing; send current paths.
        imagePath = existing?._image_path;
        displayImagePath = existing?._display_path;
      }
      const res = await startBannerCheckout({
        bannerAdId: reuseId,
        imagePath,
        displayImagePath,
        destinationUrl: url.trim(),
        targetCountryCode: targetCountryCode || null,
        targetRegionCode: targetRegionCode || null,
      });
      if (res.admin_free) {
        toast.success("Banner activated free (admin) for 7 days.");
        resetForm();
        loadAds();
      } else if (res.native) {
        toast.success("Banner activated for 7 days.");
        resetForm();
        loadAds();
      } else if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        throw new Error("Could not start checkout.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setUrl("");
    setConfirmed(false);
    setReuseId(null);
    setTargetCountryCode("");
    setTargetRegionCode("");
  };

  const runAgain = (ad) => {
    setReuseId(ad.id);
    setTargetCountryCode(ad.target_country_code || "");
    setTargetRegionCode(ad.target_region_code || "");
    setUrl(ad.destination_url || "");
    setPreview(ad.display_url);
    setFile(null);
    setConfirmed(false);
    document.getElementById("banner-create")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="advertise-page" className="min-h-[100dvh] bg-[#050505] pb-20 text-white">
      <div className="rr-on-image sticky top-0 z-30 border-b border-white/10 bg-[#050505]/90 px-5 pb-4 pt-6">
        <button
          data-testid="advertise-back"
          onClick={() => navigate(-1)}
          className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-white/60 transition-colors hover:text-white"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white">Advertise on RoadRnr</h1>
      </div>

      <div className="mx-auto max-w-md space-y-8 px-5 pt-6">
        <section>
          <h2 className="font-display text-2xl font-extrabold leading-tight tracking-tight text-[#ff2a00]">
            Put your brand in front of automotive enthusiasts.
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-white/70">
            Advertise your business, products or services across RoadRnr.
          </p>
          <div className="mt-4 inline-flex items-baseline gap-2 rounded-full border border-[#ff2a00]/40 bg-[#ff2a00]/10 px-4 py-2">
            <span className="font-display text-xl font-extrabold text-white">£9.99</span>
            <span className="text-sm font-semibold text-white/60">/ 7 days</span>
          </div>
        </section>

        {/* Create / Run again */}
        <section id="banner-create">
          <h3 className="font-display text-lg font-bold text-white">
            {reuseId ? "Run this banner again" : "Create your banner"}
          </h3>
          <p className="mt-1 text-xs text-white/50">
            Required ratio: 3:1 landscape · Recommended 1500 × 500 px · JPG, PNG or WebP · Max 5 MB
          </p>

          <label
            data-testid="banner-file"
            className="mt-3 flex aspect-[3/1] w-full cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-dashed border-white/20 bg-[#0e0e0e]"
          >
            {preview ? (
              <img src={preview} alt="Preview" className="h-full w-full object-contain" />
            ) : (
              <span className="flex flex-col items-center gap-1 text-white/40">
                <Upload className="h-5 w-5" />
                <span className="text-xs">Tap to upload banner</span>
              </span>
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickFile} />
          </label>

          <input
            data-testid="banner-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-destination.com"
            className="mt-3 w-full rounded-2xl border border-white/10 bg-[#121212] px-4 py-3 text-base sm:text-sm text-white placeholder:text-white/40 focus:outline-none"
          />

          <label className="mt-3 block text-sm text-white/70" htmlFor="banner-country">Advertise in</label>
          <select id="banner-country" data-testid="banner-country" value={targetCountryCode}
            onChange={(event) => { setTargetCountryCode(event.target.value); setTargetRegionCode(""); }}
            disabled={busy || !!reuseId}
            className="mt-2 w-full rounded-2xl border border-white/10 bg-[#121212] px-4 py-3 text-base text-white">
            <option value="">{isAdmin ? "Everywhere (admin)" : "Choose a country"}</option>
            {BANNER_COUNTRIES.map((country) =>
              <option key={country.code} value={country.code}>{country.name}</option>)}
          </select>
          <label className="mt-3 block text-sm text-white/70" htmlFor="banner-region">State / region</label>
          <select id="banner-region" data-testid="banner-region" value={targetRegionCode}
            onChange={(event) => setTargetRegionCode(event.target.value)} disabled={busy || !targetCountryCode || !!reuseId}
            className="mt-2 w-full rounded-2xl border border-white/10 bg-[#121212] px-4 py-3 text-base text-white">
            <option value="">{isAdmin ? "All regions (admin)" : "Choose a state / region"}</option>
            {regionsForCountry(bannerRegions, targetCountryCode).map((region) =>
              <option key={region.region_code} value={region.region_code}>{region.region_name}</option>)}
          </select>
          {locationsUsingSaved && <p role="status" className="mt-2 text-xs text-white/60">
            Using saved location choices. Purchasing may be temporarily unavailable while services recover.
          </p>}
          <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs text-white/60">
            <input
              type="checkbox"
              data-testid="banner-confirm"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 accent-[#ff2a00]"
            />
            <span>
              I confirm my banner complies with the RoadRnr Banner Ad Rules and I have the
              right to use the submitted content.
            </span>
          </label>

          <button
            data-testid="advertise-create-cta"
            onClick={submit}
            disabled={busy}
            className="mt-4 flex w-full items-center justify-center rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : isAdmin ? "Activate FREE · 7 days (admin)" : "Continue to payment · £9.99"}
          </button>
          {reuseId && (
            <button onClick={resetForm} className="mt-2 w-full text-center text-xs text-white/50">
              Cancel
            </button>
          )}
        </section>

        {/* Rules */}
        <section>
          <h3 className="font-display text-lg font-bold text-white">Banner Ad Rules</h3>
          <p className="mt-1 text-sm text-white/55">
            Any legitimate brand or service reasonably appropriate to RoadRnr users is welcome.
            Ads must:
          </p>
          <ul className="mt-3 space-y-2">
            {RULES.map((r) => (
              <li key={r} className="flex gap-2 text-sm text-white/70">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#ff2a00]" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-white/40">
            Your 7-day advertising period begins on activation and cannot be paused or extended
            by editing your ad.
          </p>
        </section>

        {/* Manage existing ads */}
        <section>
          <button
            data-testid="advertise-my-banner-ads"
            onClick={() => navigate("/my-banner-ads")}
            className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-[#0d0d0d] px-4 py-4 text-left transition-colors active:bg-[#141414]"
          >
            <span>
              <span className="block font-display text-base font-bold text-white">My banner ads</span>
              <span className="mt-0.5 block text-xs text-white/50">Manage, edit, end or re-run your ads</span>
            </span>
            <ChevronRight className="h-5 w-5 text-white/40" />
          </button>
        </section>
      </div>
    </div>
  );
}
